import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../axios';
import { toast } from 'react-hot-toast';

// ============ TYPES ============
export interface MediaGalleryItem {
  id: string;
  imageUrl: string;
  createdAt: string;
}

export interface MediaDocumentary {
  id: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  videoUrl: string;
  duration?: string;
  youtubeEmbed: boolean;
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface MediaProposal {
  id: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  fileUrl: string;
  fileSize?: string;
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface GalleryFilters {
  limit?: number;
  offset?: number;
}

export interface MediaFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

// ============ GALLERY HOOKS ============
export function useMediaGallery(filters?: GalleryFilters) {
  return useQuery({
    queryKey: ['media-gallery', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const { data } = await api.get(`/media/gallery?${params.toString()}`);
      return data.gallery as MediaGalleryItem[];
    },
  });
}

export function useCreateGalleryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<MediaGalleryItem>) => {
      const { data: response } = await api.post('/media/gallery', data);
      return response as MediaGalleryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-gallery'] });
      toast.success('Gallery item created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create gallery item');
    },
  });
}

export function useUpdateGalleryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<MediaGalleryItem> }) => {
      const { data: response } = await api.put(`/media/gallery/${id}`, data);
      return response as MediaGalleryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-gallery'] });
      toast.success('Gallery item updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update gallery item');
    },
  });
}

export function useDeleteGalleryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/media/gallery/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-gallery'] });
      toast.success('Gallery item deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete gallery item');
    },
  });
}

// ============ DOCUMENTARIES HOOKS ============
export function useMediaDocumentaries(filters?: MediaFilters) {
  return useQuery({
    queryKey: ['media-documentaries', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const { data } = await api.get(`/media/documentaries?${params.toString()}`);
      return data.documentaries as MediaDocumentary[];
    },
  });
}

export function useCreateDocumentary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<MediaDocumentary>) => {
      const { data: response } = await api.post('/media/documentaries', data);
      return response as MediaDocumentary;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-documentaries'] });
      toast.success('Documentary created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create documentary');
    },
  });
}

export function useUpdateDocumentary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<MediaDocumentary> }) => {
      const { data: response } = await api.put(`/media/documentaries/${id}`, data);
      return response as MediaDocumentary;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-documentaries'] });
      toast.success('Documentary updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update documentary');
    },
  });
}

export function useDeleteDocumentary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/media/documentaries/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-documentaries'] });
      toast.success('Documentary deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete documentary');
    },
  });
}

// ============ PROPOSALS HOOKS ============
export function useMediaProposals(filters?: MediaFilters) {
  return useQuery({
    queryKey: ['media-proposals', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const { data } = await api.get(`/media/proposals?${params.toString()}`);
      return data.proposals as MediaProposal[];
    },
  });
}

export function useCreateProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<MediaProposal>) => {
      const { data: response } = await api.post('/media/proposals', data);
      return response as MediaProposal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-proposals'] });
      toast.success('Proposal created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create proposal');
    },
  });
}

export function useUpdateProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<MediaProposal> }) => {
      const { data: response } = await api.put(`/media/proposals/${id}`, data);
      return response as MediaProposal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-proposals'] });
      toast.success('Proposal updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update proposal');
    },
  });
}

export function useDeleteProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/media/proposals/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-proposals'] });
      toast.success('Proposal deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete proposal');
    },
  });
}

// ============ UPLOAD HOOKS ============
export function useUploadMediaImage() {
  return useMutation({
    mutationFn: async ({ file, folder }: { file: File; folder?: string }) => {
      const formData = new FormData();
      formData.append('image', file);
      if (folder) formData.append('folder', folder);

      const { data } = await api.post('/media/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data as { url: string; filename: string; size: string };
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to upload image');
    },
  });
}

export function useUploadMediaFile() {
  return useMutation({
    mutationFn: async ({ file, folder }: { file: File; folder?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (folder) formData.append('folder', folder);

      const { data } = await api.post('/media/upload/file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data as { url: string; filename: string; size: string };
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to upload file');
    },
  });
}
